import { useGetDatasetInfo } from "api/api-hooks/datasets-hooks";
import { totalTypeDropdownSelectorOptions } from "components/dashboard/dataset-table/dataset-table-editor/defaultDatasetTableEditorConfigs";
import { DatasetFullDefaultConfig } from "models/dataset-models";
import { Button } from "primereact/button";
import { ColorPicker } from "primereact/colorpicker";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import React, { lazy, Suspense, useState } from "react";

const HighlightsEditor = lazy(
  () =>
    import(
      "components/dashboard/dataset-table/dataset-table-editor/highlights-editor/HighlightsEditor"
    )
);

interface DatasetTableDatasetEditorProps {
  currentConfig: any;
  setCurrentConfig: React.Dispatch<
    React.SetStateAction<DatasetFullDefaultConfig>
  >;
  onConfigChange: any;
}

enum DefaultHighlightColor {
  defaultTextColor = "#000000",
  defaultBackgroundColor = "#FFFFFF",
}

const DatasetTableDatasetEditor = ({
  currentConfig,
  setCurrentConfig,
  onConfigChange,
}: DatasetTableDatasetEditorProps) => {
  const [highlightDialogIdentifier, setHighlightDialogIdentifier] =
    useState("");
  const [highlightDialogVisible, setHighlightDialogVisible] = useState(false);
  const [showTotal, setShowTotal] = useState(false);

  const datasetsQueryData = currentConfig.datasets?.map((it: any) =>
    useGetDatasetInfo(it.identifier)
  );
  const datasetInfos = datasetsQueryData?.map((it: any) => it.data);

  const textEditor = (options: any) => {
    return (
      <InputText
        value={options.value || ""}
        onChange={(e) => options.editorCallback(e.target.value)}
        style={{ width: "100%" }}
      />
    );
  };

  const numberEditor = (options: any) => {
    return (
      <InputNumber
        value={options.value}
        onChange={(e) => options.editorCallback(e.value)}
        inputStyle={{ width: "100%" }}
      />
    );
  };

  const switchEditor = (options: any) => {
    return (
      <InputSwitch
        checked={options.value}
        onChange={(e) => {
          options.editorCallback(e.value);
          setShowTotal(e.value);
        }}
      />
    );
  };

  const totalTypeEditor = (options: any) => {
    if (showTotal) {
      return (
        <Dropdown
          value={options.value}
          options={totalTypeDropdownSelectorOptions}
          onChange={(e) => options.editorCallback(e.value)}
          style={{ width: "100%" }}
        />
      );
    } else {
      return <Dropdown disabled style={{ width: "100%" }} />;
    }
  };

  const defaultHighlightEditor = (options: any) => {
    return (
      <div className="flex justify-content-center align-items-center">
        <ColorPicker
          value={
            options.rowData.defaultHighlight?.color ??
            DefaultHighlightColor.defaultTextColor
          }
          onChange={(e) =>
            options.editorCallback({
              color: "#" + e.value,
              bgColor: options.rowData.defaultHighlight?.bgColor,
            })
          }
        />
        <ColorPicker
          value={
            options.rowData.defaultHighlight?.bgColor ??
            DefaultHighlightColor.defaultBackgroundColor
          }
          onChange={(e) =>
            options.editorCallback({
              color: options.rowData.defaultHighlight?.color,
              bgColor: "#" + e.value,
            })
          }
        />
        <i
          className="pi pi-refresh ml-2 cursor-pointer"
          onClick={() =>
            options.editorCallback({
              color: DefaultHighlightColor.defaultTextColor,
              bgColor: DefaultHighlightColor.defaultBackgroundColor,
            })
          }
        />
      </div>
    );
  };

  const highlightBodyTemplate = (rowData: any) => {
    return (
      <Button
        label={"Show"}
        className={"p-button-sm p-button-text p-button-outlined"}
        onClick={() => handleShowHighlightsClicked(rowData.identifier)}
      />
    );
  };

  const defaultHighlightBodyTemplate = (rowData: any) => {
    let tempColor = rowData.defaultHighlight
      ? rowData.defaultHighlight?.color
      : "#000000";
    let tempBgColor = rowData.defaultHighlight
      ? rowData.defaultHighlight?.bgColor
      : "#FFFFFF";
    tempColor = tempColor.startsWith("#") ? tempColor : "#" + tempColor;
    tempBgColor = tempBgColor.startsWith("#") ? tempBgColor : "#" + tempBgColor;
    return (
      <span
        className="p-1 pl-2 pr-2"
        style={{ color: tempColor, backgroundColor: tempBgColor }}
      >
        Text
      </span>
    );
  };

  const handleShowHighlightsClicked = (identifier: string) => {
    setHighlightDialogVisible(true);
    setHighlightDialogIdentifier(identifier);
  };

  const onRowEditComplete = (e: any) => {
    if (currentConfig.datasets) {
      let datasetsToUpdate = [...currentConfig.datasets!];
      let { newData, index } = e;

      if (newData.defaultHighlight) {
        if (newData.defaultHighlight.bgColor === undefined) {
          newData.defaultHighlight.bgColor =
            DefaultHighlightColor.defaultBackgroundColor;
        }
        if (newData.defaultHighlight.color === undefined) {
          newData.defaultHighlight.color =
            DefaultHighlightColor.defaultTextColor;
        }
      }

      datasetsToUpdate[index] = newData;
      setCurrentConfig({ ...currentConfig, datasets: datasetsToUpdate });
      onConfigChange({ ...currentConfig, datasets: datasetsToUpdate });
    } else {
      let { newData } = e;

      if (newData.defaultHighlight) {
        if (newData.defaultHighlight.bgColor === undefined) {
          newData.defaultHighlight.bgColor =
            DefaultHighlightColor.defaultBackgroundColor;
        }
        if (newData.defaultHighlight.color === undefined) {
          newData.defaultHighlight.color =
            DefaultHighlightColor.defaultTextColor;
        }
      }

      setCurrentConfig(newData);
    }
  };

  const identifierBodyTemplate = (rowData: any) => {
    if (currentConfig.datasets) {
      let datasetInfo = datasetInfos?.find(
        (it: any) => it?.identifier === rowData.identifier
      );
      return <span>{datasetInfo?.title}</span>;
    } else {
      return <span>{rowData.identifier}</span>;
    }
  };

  const handleRowReorder = (e: any) => {
    setCurrentConfig({ ...currentConfig, datasets: e.value });
    onConfigChange({ ...currentConfig, datasets: e.value });
  };

  return (
    <>
      {currentConfig.datasets ? (
        <>
          <DataTable
            value={currentConfig.datasets}
            editMode="row"
            onRowEditComplete={onRowEditComplete}
            tableStyle={{ width: "100%" }}
            reorderableRows
            onRowReorder={(e) => handleRowReorder(e)}
          >
            <Column rowReorder style={{ width: "5%" }} />
            <Column
              className="p-0"
              field={"identifier"}
              header={"Identifier"}
              style={{ width: "25%" }}
              align={"center"}
              body={identifierBodyTemplate}
            />
            <Column
              className="p-0"
              field={"decimalPlaces"}
              header={"Decimal"}
              style={{ width: "5%" }}
              align={"center"}
              editor={(options) => numberEditor(options)}
            />
            <Column
              className="p-0"
              field={"overriddenTitle"}
              header={"Title"}
              style={{ width: "15%" }}
              align={"center"}
              editor={(options) => textEditor(options)}
            />
            <Column
              className="p-0"
              field={"description"}
              header={"Description"}
              style={{ width: "25%" }}
              align={"center"}
              editor={(options) => textEditor(options)}
            />
            <Column
              className="p-0"
              field={"showTotal"}
              header={"Show total"}
              style={{ width: "10%" }}
              align={"center"}
              editor={(options) => switchEditor(options)}
            />
            <Column
              className="p-0"
              field={"totalType"}
              header={"Total type"}
              style={{ width: "10%" }}
              align={"center"}
              editor={(options) => totalTypeEditor(options)}
            />
            <Column
              className="p-0"
              field={"defaultHighlight"}
              style={{ width: "10%" }}
              header={"Default highlight"}
              headerStyle={{ width: "10%" }}
              align={"center"}
              editor={(options) => defaultHighlightEditor(options)}
              body={defaultHighlightBodyTemplate}
            />
            <Column
              className="p-0"
              style={{ width: "10%" }}
              header={"Highlight ranges"}
              headerStyle={{ width: "10%" }}
              align={"center"}
              body={highlightBodyTemplate}
            />
            <Column
              className="p-0"
              rowEditor
              style={{ width: "10%" }}
              headerStyle={{ width: "10%" }}
              bodyStyle={{ textAlign: "center" }}
            />
          </DataTable>
          <Dialog
            onHide={() => setHighlightDialogVisible(false)}
            visible={highlightDialogVisible}
            header={"Edit highlights"}
            style={{ width: "min(100vw, 800px)", height: "500x" }}
          >
            <Suspense fallback={<ProgressSpinner />}>
              <HighlightsEditor
                currentConfig={currentConfig}
                setCurrentConfig={setCurrentConfig}
                onConfigChange={onConfigChange}
                identifier={highlightDialogIdentifier}
              />
            </Suspense>
          </Dialog>
        </>
      ) : (
        <>
          <DataTable
            value={[currentConfig]}
            editMode="row"
            onRowEditComplete={onRowEditComplete}
            tableStyle={{ width: "100%" }}
          >
            <Column
              className="p-0"
              field={"identifier"}
              header={"Identifier"}
              style={{ width: "30%" }}
              align={"center"}
              body={identifierBodyTemplate}
            />
            <Column
              className="p-0"
              field={"decimalPlaces"}
              header={"Decimal"}
              style={{ width: "5%" }}
              align={"center"}
              editor={(options) => numberEditor(options)}
            />
            <Column
              className="p-0"
              field={"overriddenTitle"}
              header={"Title"}
              style={{ width: "15%" }}
              align={"center"}
              editor={(options) => textEditor(options)}
            />
            <Column
              className="p-0"
              field={"description"}
              header={"Description"}
              style={{ width: "25%" }}
              align={"center"}
              editor={(options) => textEditor(options)}
            />
            <Column
              className="p-0"
              field={"showTotal"}
              header={"Show total"}
              style={{ width: "10%" }}
              align={"center"}
              editor={(options) => switchEditor(options)}
            />
            <Column
              className="p-0"
              field={"totalType"}
              header={"Total type"}
              style={{ width: "10%" }}
              align={"center"}
              editor={(options) => totalTypeEditor(options)}
            />
            <Column
              className="p-0"
              field={"defaultHighlight"}
              style={{ width: "10%" }}
              header={"Default highlight"}
              headerStyle={{ width: "10%" }}
              align={"center"}
              editor={(options) => defaultHighlightEditor(options)}
              body={defaultHighlightBodyTemplate}
            />
            <Column
              className="p-0"
              style={{ width: "10%" }}
              header={"Highlight ranges"}
              headerStyle={{ width: "10%" }}
              align={"center"}
              body={highlightBodyTemplate}
            />
            <Column
              className="p-0"
              rowEditor
              style={{ width: "10%" }}
              headerStyle={{ width: "10%" }}
              bodyStyle={{ textAlign: "center" }}
            />
          </DataTable>
          <Dialog
            onHide={() => setHighlightDialogVisible(false)}
            visible={highlightDialogVisible}
            header={"Edit highlights"}
            style={{ width: "min(100vw, 800px)", height: "500x" }}
          >
            <Suspense fallback={<ProgressSpinner />}>
              <HighlightsEditor
                currentConfig={currentConfig}
                setCurrentConfig={setCurrentConfig}
                onConfigChange={onConfigChange}
                identifier={highlightDialogIdentifier}
              />
            </Suspense>
          </Dialog>
        </>
      )}
    </>
  );
};

export default DatasetTableDatasetEditor;
